import { syncManager } from '../../../services/sync';

/**
 * Referral Follow-up Automation System
 * Phase B Artifact 10: Automated email follow-up sequences
 */

export interface ReferralAutomationConfig {
  referralRequestId: string;
  recipientEmail: string;
  recipientName: string;
  userName: string;
  referralLink: string;
  referralCount: number;
  acceptedCount: number;
  day: number;
  emailTemplate: string;
  subject: string;
  timing: string;
  status: 'pending' | 'sent' | 'failed' | 'snoozed';
  retryCount: number;
  snoozeDate?: Date;
}

export interface ReferralAutomationResult {
  success: boolean;
  emailId?: string;
  error?: string;
  config: ReferralAutomationConfig;
}

export class ReferralAutomation {
  private automationQueue: ReferralAutomationConfig[] = [];

  /**
   * Schedule email for specific day
   */
  scheduleEmail(config: Omit<ReferralAutomationConfig, 'status' | 'retryCount'>): void {
    const automationConfig: ReferralAutomationConfig = {
      ...config,
      status: 'pending',
      retryCount: 0,
    };

    this.automationQueue.push(automationConfig);
  }

  /**
   * Send all pending emails
   */
  async sendPendingEmails(): Promise<ReferralAutomationResult[]> {
    const results: ReferralAutomationResult[] = [];

    for (const config of this.automationQueue) {
      if (config.status === 'pending') {
        try {
          const result = await this.sendEmail(config);
          results.push(result);
        } catch (error) {
          results.push({
            success: false,
            error: error.message,
            config,
          });
        }
      }
    }

    return results;
  }

  /**
   * Send email to recipient
   */
  private async sendEmail(
    config: ReferralAutomationConfig
  ): Promise<ReferralAutomationResult> {
    try {
      // Personalize template
      const personalizedTemplate = this.personalizeTemplate(
        config.emailTemplate,
        {
          friendName: config.recipientName,
          userName: config.userName,
          link: config.referralLink,
          referralCount: config.referralCount,
          acceptedCount: config.acceptedCount,
        }
      );

      // Log to CRM_Leads
      await this.logToCRM(config, 'Sent');

      // Return success result
      return {
        success: true,
        emailId: `EMAIL-${Date.now()}`,
        config,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        config,
      };
    }
  }

  /**
   * Log email to CRM_Leads tab
   */
  private async logToCRM(
    config: ReferralAutomationConfig,
    status: 'Sent' | 'Failed' | 'Snoozed'
  ): Promise<void> {
    await syncManager.updateTabData('CRM_Leads', {
      Project_ID: config.referralRequestId,
      Email: config.recipientEmail,
      Status: `Follow-up Email ${status}`,
      Notes: `Referral follow-up email sent on Day ${config.day}: ${config.subject}`,
      Email_Date: new Date().toISOString(),
      Email_Status: status,
      Email_Subject: config.subject,
      Email_Body: config.emailTemplate,
      Referral_Request_ID: config.referralRequestId,
    });
  }

  /**
   * Personalize template with placeholders
   */
  private personalizeTemplate(
    template: string,
    replacements: Record<string, string>
  ): string {
    let personalized = template;
    for (const [key, value] of Object.entries(replacements)) {
      personalized = personalized.replace(`{${key}}`, value);
    }
    return personalized;
  }

  /**
   * Get all scheduled emails
   */
  getScheduledEmails(): ReferralAutomationConfig[] {
    return this.automationQueue.filter(
      config => config.status === 'pending'
    );
  }

  /**
   * Get email status
   */
  getEmailStatus(referralRequestId: string): string | null {
    const config = this.automationQueue.find(
      config => config.referralRequestId === referralRequestId
    );
    return config ? config.status : null;
  }

  /**
   * Set email as snoozed
   */
  async snoozeEmail(referralRequestId: string, snoozeDays: number): Promise<void> {
    const config = this.automationQueue.find(
      config => config.referralRequestId === referralRequestId
    );
    if (config) {
      config.status = 'snoozed';
      config.snoozeDate = new Date(Date.now() + snoozeDays * 24 * 60 * 60 * 1000);
      await this.logToCRM(config, 'Snoozed');
    }
  }

  /**
   * Reset email status to pending (for retry)
   */
  async resetEmailStatus(referralRequestId: string): Promise<void> {
    const config = this.automationQueue.find(
      config => config.referralRequestId === referralRequestId
    );
    if (config) {
      config.status = 'pending';
      config.retryCount = 0;
    }
  }

  /**
   * Clear all scheduled emails
   */
  clearAllEmails(): void {
    this.automationQueue = [];
  }
}

// Singleton instance
export const referralAutomation = new ReferralAutomation();

/**
 * Schedule referral email for Day 1
 */
export const scheduleReferralRequestEmail = (
  referralRequestId: string,
  recipientEmail: string,
  recipientName: string,
  userName: string,
  referralLink: string
) => {
  const config = {
    referralRequestId,
    recipientEmail,
    recipientName,
    userName,
    referralLink,
    referralCount: 1,
    acceptedCount: 0,
    day: 1,
    emailTemplate: `Hi {friendName}, I wanted to share something special with you — I recently joined Chrysalinks and they're helping me build my online presence. They have a really cool referral program where you can get a free website if you refer friends!

Here's your personalized referral link: {link}

What's the process?
1. Click the link to create your account
2. Share it with 5 friends
3. Once they accept the offer, you unlock your own FREE website

No pressure at all — just wanted to let you know in case you're interested!

Best,
{userName}`,
    subject: '🎁 Quick favor from {userName}',
    timing: 'Day 1 (immediate)',
  };

  referralAutomation.scheduleEmail(config);
};

/**
 * Schedule follow-up email for Day 3
 */
export const scheduleFollowUpEmail = (
  referralRequestId: string,
  recipientEmail: string,
  recipientName: string,
  userName: string,
  referralLink: string,
  referralCount: number,
  acceptedCount: number
) => {
  const config = {
    referralRequestId,
    recipientEmail,
    recipientName,
    userName,
    referralLink,
    referralCount,
    acceptedCount,
    day: 3,
    emailTemplate: `Hi {friendName},

I hope you're doing well!

Just wanted to quickly follow up on that referral link I shared — I'm looking to reach my 5-referral goal and would love your help.

The process is simple:
✅ Click the link to sign up
✅ Share it with your contacts
✅ Get your FREE website when 5 people accept

If you've already shared it, thanks so much — that means a lot to me!

If not, no worries at all — just wanted to remind you it's there if you ever need it.

Talk soon,
{userName}`,
    subject: '👋 Quick follow-up',
    timing: 'Day 3',
  };

  referralAutomation.scheduleEmail(config);
};

/**
 * Schedule mid-process encouragement email for Day 7
 */
export const scheduleEncouragementEmail = (
  referralRequestId: string,
  recipientEmail: string,
  recipientName: string,
  userName: string,
  referralLink: string,
  referralCount: number,
  acceptedCount: number
) => {
  const config = {
    referralRequestId,
    recipientEmail,
    recipientName,
    userName,
    referralLink,
    referralCount,
    acceptedCount,
    day: 7,
    emailTemplate: `Hi {friendName},

Just wanted to celebrate — you're making great progress with the referrals!

I'm now at {referralCount}/5 referrals, and every accepted one brings me closer to my FREE website. You've already helped me get {acceptedCount} referrals, which is amazing!

The link is still active: {link}

Once we hit 5 total, we both unlock our free websites. Let me know if you need any help sharing it further.

Keep crushing it,
{userName}`,
    subject: '🎉 Great progress!',
    timing: 'Day 7',
  };

  referralAutomation.scheduleEmail(config);
};

/**
 * Schedule urgency email for Day 10
 */
export const scheduleUrgencyEmail = (
  referralRequestId: string,
  recipientEmail: string,
  recipientName: string,
  userName: string,
  referralLink: string
) => {
  const config = {
    referralRequestId,
    recipientEmail,
    recipientName,
    userName,
    referralLink,
    referralCount: 4,
    acceptedCount: 0,
    day: 10,
    emailTemplate: `Hi {friendName},

Quick favor — I'm one referral away from securing my free website! 🎉

Could you help me out by sharing the link with 1-2 more people? Even just posting it on social media or sharing it in a group chat would help.

Link: {link}

I'd really appreciate your help — it would mean so much to me!

Thanks in advance,
{userName}`,
    subject: '🎯 One referral away!',
    timing: 'Day 10',
  };

  referralAutomation.scheduleEmail(config);
};

/**
 * Schedule milestone celebration email for Day 12
 */
export const scheduleMilestoneEmail = (
  referralRequestId: string,
  recipientEmail: string,
  recipientName: string,
  userName: string,
  referralLink: string,
  referralCount: number
) => {
  const config = {
    referralRequestId,
    recipientEmail,
    recipientName,
    userName,
    referralLink,
    referralCount,
    acceptedCount: referralCount,
    day: 12,
    emailTemplate: `Hi {friendName},

🎉 **MASSIVE NEWS!** I just hit 5 accepted referrals!

That means I've officially unlocked my FREE website from Chrysalinks! You helped me get {referralCount} of those referrals — your support means the world to me.

The journey is awesome, and I've got a kickstarter to complete my website questionnaire. Once that's done, AI will generate my design in about 10 minutes.

If you've shared the link with anyone, thank you SO much! If not, no worries at all — you've already been a huge help.

Let's keep the momentum going!
{userName}`,
    subject: '🎉 I hit 5 referrals!',
    timing: 'Day 12',
  };

  referralAutomation.scheduleEmail(config);
};

/**
 * Schedule post-referral success email for Day 14+
 */
export const schedulePostReferralEmail = (
  referralRequestId: string,
  recipientEmail: string,
  recipientName: string,
  userName: string,
  referralLink: string
) => {
  const config = {
    referralRequestId,
    recipientEmail,
    recipientName,
    userName,
    referralLink,
    referralCount: 1,
    acceptedCount: 0,
    day: 14,
    emailTemplate: `Hi {friendName},

🎉 Can't believe it — you've accepted the referral!

Thanks a million for supporting me. This brings me one step closer to my FREE website.

Here's your referral link (share it with 4 more friends to unlock yours): {link}

We're both on the same journey now — let's get those referrals rolling!

Talk soon,
{userName}`,
    subject: '🎉 Referral accepted!',
    timing: 'Day 14+',
  };

  referralAutomation.scheduleEmail(config);
};
